/* @flow */

import 'isomorphic-fetch';

import * as Sentry from '@sentry/react';
import { loginPath } from 'src/shared/app/base/route/setting/routePaths';
import { isFunction } from '@braindate/util/lib/type';
import { getEventId } from 'src/shared/domain/event/selector/eventSelectors';
import { serializeUrl } from 'src/shared/core/util/urlUtil';
import { removeTokenFromCookie } from 'src/shared/app/base/util/tokenUtils';
import { getAuthenticationToken } from 'src/shared/app/authentication/selector/base/appAuthenticationBaseSelectors';
import { spanStatusfromHttpCode } from '@sentry/tracing';
import { unregisterSentryUserFromBrowser } from 'src/shared/app/base/util/sentryUtils';
import {
  getEnvironment,
  getAPIURL,
  isErrorReportingEnabled,
  getVersion,
} from 'src/shared/app/base/selector/appEnvSelectors';

type methods = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

function getUrl(getState, url) {
  const extractUrl = () => {
    if (isFunction(url)) {
      return url(getAPIURL(getState()), getEventId(getState()));
    }
    return url;
  };
  return extractUrl().replace(new RegExp('[0-9]+', 'g'), 'X');
}

function catchFetchError(getState, url, span) {
  return (e) => {
    const { status, url: newUrl } = e;
    span.setStatus(spanStatusfromHttpCode(status));

    const error = new Error(`Failed to fetch ${getUrl(getState, url)}`);
    // $FlowIssue
    error.status = status;
    // $FlowIssue
    error.url = newUrl;
    throw error;
  };
}
function doFetch(
  getState: () => Object,
  url: any,
  data?: Object,
  method?: methods = 'GET',
) {
  const transaction = Sentry.startTransaction({
    name: `${method} ${getUrl(getState, url)}`,
    op: 'http.api',
    data,
  });

  return _fetch(getState, url, data, method)
    .catch(catchFetchError(getState, url, transaction))
    .then((response) => {
      transaction.setStatus(spanStatusfromHttpCode(response.status));
      transaction.setHttpStatus(response.status);
      transaction.finish();
      return response;
    });
}

// HTTP methods
export function get(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return doFetch(getState, url, data);
}

export function post(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return doFetch(getState, url, data, 'POST');
}

export function patch(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return doFetch(getState, url, data, 'PATCH');
}

export function put(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Response> {
  return doFetch(getState, url, data, 'PUT');
}

export function deleteFn(
  getState: () => Object,
  url: any,
  data?: Object = {},
): Promise<Response> {
  return doFetch(getState, url, data, 'DELETE');
}

export function smartGet(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Object> {
  return doFetch(getState, url, data, 'GET').then(parseResponse);
}

export function smartPost(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Object> {
  return doFetch(getState, url, data, 'POST').then(parseResponse);
}

export function smartDelete(
  getState: () => Object,
  url: any,
  data?: Object,
): Promise<Object> {
  return doFetch(getState, url, data, 'DELETE').then(parseResponse);
}

export type BraindateError = Error & {
  status: string,
};

// Parse

export function parseResponse(response: Response): any {
  const { url, status, statusText } = response;
  // If status code is 204, don't parse it to JSON or it causes an error. Return
  // an empty object instead of the response, otherwise actions may try to
  // save the response directly in the redux state, which will throw an
  // exception
  if (status === 204) {
    return {};
  } else if (status >= 200 && status < 300) {
    return response.json();
  }

  const errorName = 'APIError';

  return response
    .json()
    .catch((e) => {
      const error = new Error(
        `Failed to parse JSON response from ${url}
          Status: ${status} ${statusText}
          Original error: ${e.toString()}`,
      );

      error.name = errorName;
      // $FlowIssue
      error.status = status;
      // $FlowIssue
      error.url = url;

      throw error;
    })
    .then((json) => {
      const error = new Error(
        `Call to ${url} failed
          Status: ${status} ${statusText}
          With message: ${JSON.stringify(json)}`,
      );

      error.name = errorName;
      // $FlowIssue
      error.status = status;
      // $FlowIssue
      error.url = url;
      // $FlowIssue
      error.json = json;

      throw error;
    });
}

/*
|------------------------------------------------------------------------------
| PRIVATE FUNCTIONS
|------------------------------------------------------------------------------
*/

const _fetch = (
  getState: () => Object,
  url: any,
  data?: Object,
  method?: methods = 'GET',
): Promise<Response> => {
  const params = {
    method,
    headers: {},
  };

  const state = getState();
  const token = getAuthenticationToken(state);
  const { language } = state.app;

  let _url =
    typeof url === 'function'
      ? url(getAPIURL(state), getEventId(getState()))
      : url;

  if (token) {
    params.headers.Authorization = `Token ${token}`;
  }

  if (language) {
    params.headers['Accept-Language'] = language;
  }

  // Update user agent for calls made to the API by the node server
  if (typeof window === 'undefined') {
    params.headers['User-Agent'] = `${getVersion(state)} ${getEnvironment(
      state,
    )}`;
  }

  if (data && typeof data === 'object') {
    if (method === 'GET') {
      _url = serializeUrl(_url, data);
    } else if (typeof window !== 'undefined' && data instanceof FormData) {
      // $FlowIssue
      params.body = data;
    } else {
      params.headers['Content-Type'] = 'application/json';

      // $FlowIssue
      params.body = JSON.stringify(data);
    }
  }

  return fetch(_url, params).then((response: Response) => {
    const { status, url } = response;
    const errorName = 'Fetch error';
    // If invalid token (401 UNAUTHORIZED)
    if (status === 401) {
      if (typeof window !== 'undefined') {
        removeTokenFromCookie();

        if (isErrorReportingEnabled(state)) {
          unregisterSentryUserFromBrowser();
        }

        // Using `getRoutePath(loginRoute);` with `loginRoute` was creating
        // a cycle dependency problem here
        window.location = loginPath;
      } else {
        const error = new Error('Invalid token');

        // $FlowIssue
        error.name = errorName;
        // $FlowIssue
        error.status = status;
        // $FlowIssue
        error.url = url;

        throw error;
      }
    }

    return response;
  });
};
